<template>
  <div class="container">
    <div class="header"><span class="back" @click="$router.push('/coupon')">&#8592;</span><span class="title">优惠券管理</span></div>
    <div class="card">
      <div class="label">停车场名称</div>
      <div class="field" style="padding:0;">
        <div class="select-wrapper">
          <input class="select-input" v-model="parkingSearch" placeholder="搜索停车场名称" @input="filterParkings" />
          <div class="select-display" v-if="selectedParking">{{ selectedParking.name }}</div>
          <div class="dropdown" v-if="showDropdown">
            <div class="option" v-for="p in filteredParkings" :key="p.id" @click="chooseParking(p)">{{ p.name }}</div>
            <div class="option empty" v-if="filteredParkings.length===0">无匹配结果</div>
          </div>
        </div>
      </div>
    </div>
    <div class="tabs">
      <span 
        class="tab" 
        :class="{ active: activeTab === 'all' }" 
        @click="switchTab('all')"
      >全部</span>
      <span 
        class="tab" 
        :class="{ active: activeTab === 'TIME' }" 
        @click="switchTab('TIME')"
      >时长券</span>
      <span 
        class="tab" 
        :class="{ active: activeTab === 'CASH' }" 
        @click="switchTab('CASH')"
      >金额券</span>
      <span 
        class="tab" 
        :class="{ active: activeTab === 'PERCENT' }" 
        @click="switchTab('PERCENT')"
      >折扣券</span>
      <span 
        class="tab" 
        :class="{ active: activeTab === 'FREE' }" 
        @click="switchTab('FREE')"
      >全免券</span>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <div class="loading-text">加载中...</div>
    </div>
    
    <!-- 优惠券卡片列表 -->
    <div v-else-if="filteredCoupons.length > 0" class="coupon-cards">
      <div 
        v-for="coupon in filteredCoupons" 
        :key="coupon.templateId" 
        class="coupon-card"
        :class="getCouponCardClass(coupon.couponType)"
        @click="() => {}"
      >
        <div class="coupon-title">{{ getCouponTypeName(coupon.couponType) }}</div>
        <div class="coupon-value">{{ formatCouponValue(coupon) }}</div>
        <div class="coupon-validity">{{ coupon.validityPeriod }}</div>
      </div>
    </div>
    
    <!-- 空状态 -->
    <div v-else class="empty">
      <div class="empty-text">暂无优惠券数据</div>
    </div>
    
    <button class="btn" @click="$router.push('/coupon/create2')">自定义创建优惠券</button>
  </div>
</template>

<script>
import { getCouponList, getCouponTypes } from '@/api/coupon'
import { getParkingList } from '@/api/parking'

export default {
  name: 'CouponManage',
  data() {
    return {
      loading: false,
      activeTab: 'all',
      coupons: [],
      // 停车场选择相关
      parkingSearch: '',
      parkings: [],
      filteredParkings: [],
      selectedParking: null,
      showDropdown: false,
      couponTypes: getCouponTypes()
    }
  },
  computed: {
    filteredCoupons() {
      if (this.activeTab === 'all') {
        return this.coupons
      }
      return this.coupons.filter(coupon => this.normalizeType(coupon.couponType) === this.activeTab)
    },
  },
  mounted() {
    this.loadCoupons()
    this.loadParkings()
  },
  methods: {
    async loadParkings() {
      try {
        const res = await getParkingList()
        const list = (res && (res.data || res.rows || res)) || []
        this.parkings = Array.isArray(list) ? list : []
        this.filteredParkings = this.parkings
        this.showDropdown = false
      } catch (e) {
        this.parkings = []
        this.filteredParkings = []
      }
    },
    filterParkings() {
      const kw = (this.parkingSearch || '').trim()
      this.showDropdown = true
      if (!kw) { this.filteredParkings = this.parkings; return }
      const lower = kw.toLowerCase()
      this.filteredParkings = this.parkings.filter(p => (p.name || '').toLowerCase().includes(lower))
    },
    chooseParking(p) {
      this.selectedParking = p
      this.parkingSearch = p.name || ''
      this.filteredParkings = this.parkings
      this.showDropdown = false
      // 选择车场后，按车场名称刷新优惠券列表
      this.loadCoupons({ parkingName: p.name })
    },
    async loadCoupons(query = {}) {
      this.loading = true
      try {
        const response = await getCouponList(query)
        this.coupons = response.rows || []
        
        // 如果没有数据，使用测试数据
        if (this.coupons.length === 0) {
          this.coupons = [
            {
              templateId: 1,
              couponName: '代金券',
              couponType: 'CASH',
              discountAmount: 50,
              validityPeriod: '有效期至2024.12.31'
            },
            {
              templateId: 2,
              couponName: '时长券',
              couponType: 'TIME',
              discountAmount: 120, // 2小时 = 120分钟
              validityPeriod: '有效期至2024.12.31'
            },
            {
              templateId: 3,
              couponName: '全免券',
              couponType: 'FREE',
              discountAmount: 0,
              validityPeriod: '有效期至2024.12.31'
            }
          ]
        }
      } catch (error) {
        // 使用测试数据作为后备
        this.coupons = [
          {
            templateId: 1,
            couponName: '代金券',
            couponType: 'CASH',
            discountAmount: 50,
            validityPeriod: '有效期至2024.12.31'
          },
          {
            templateId: 2,
            couponName: '时长券',
            couponType: 'TIME',
            discountAmount: 120,
            validityPeriod: '有效期至2024.12.31'
          },
          {
            templateId: 3,
            couponName: '全免券',
            couponType: 'FREE',
            discountAmount: 0,
            validityPeriod: '有效期至2024.12.31'
          }
        ]
      } finally {
        this.loading = false
      }
    },
    switchTab(tab) {
      this.activeTab = tab
    },
    getCouponTypeName(type) {
      const t = this.normalizeType(type)
      return this.couponTypes[t] || (
        t === 'TIME' ? '时长券' :
        t === 'CASH' ? '金额券' :
        t === 'PERCENT' ? '折扣券' :
        t === 'FREE' ? '全免券' : t
      )
    },
    formatCouponValue(coupon) {
      const amount = coupon.discountAmount
      const t = this.normalizeType(coupon.couponType)
      switch (t) {
        case 'CASH':
          return `¥${amount}`
        case 'TIME': {
          const hours = Math.floor(amount / 60)
          const minutes = amount % 60
          if (hours > 0) {
            return minutes > 0 ? `${hours}小时${minutes}分钟` : `${hours}小时`
          }
          return `${amount}分钟`
        }
        case 'FREE':
          return '全免'
        case 'PERCENT':
          return `${amount}折`
        default:
          return amount
      }
    },
    getCouponCardClass(couponType) {
      const t = this.normalizeType(couponType)
      switch (t) {
        case 'CASH':
          return 'cash-card'
        case 'TIME':
          return 'time-card'
        case 'FREE':
          return 'free-card'
        case 'PERCENT':
          return 'percent-card'
        default:
          return 'default-card'
      }
    },
    normalizeType(type) {
      if (type === 'DURATION') return 'TIME'
      if (type === 'AMOUNT') return 'CASH'
      if (type === 'DISCOUNT') return 'PERCENT'
      return type
    },
    // viewCouponDetail(coupon) {
    //   this.$router.push(`/coupon/detail/${coupon.templateId}`)
    // }
  }
}
</script>

<style scoped>
.container {
  max-width: 430px;
  margin: 0 auto;
  padding-bottom: 80px;
  background: #f7f8fa;
  min-height: 100vh;
}

.header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
}

.back {
  position: absolute;
  left: 0;
  padding-left: 16px;
  font-size: 26px;
  cursor: pointer;
}

.title {
  font-size: 20px;
  font-weight: bold;
}

.card {
  background: #fff;
  border-radius: 16px;
  margin: 18px 16px 0;
  padding: 18px 16px;
  box-shadow: 0 2px 8px #f0f0f0;
}

.label {
  font-size: 15px;
  color: #bbb;
  margin-bottom: 6px;
}

.field {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 17px;
  color: #222;
}

.select-wrapper { position: relative; }
.select-input { width: 100%; box-sizing: border-box; border: none; outline: none; background: #f7f8fa; padding: 12px 16px; border-radius: 10px; font-size: 16px; }
.select-display { display:none; }
.dropdown { position: absolute; top: 48px; left: 0; right: 0; background: #fff; border: 1px solid #eee; border-radius: 10px; max-height: 220px; overflow: auto; box-shadow: 0 6px 20px rgba(0,0,0,0.08); z-index: 10; }
.option { padding: 10px 12px; cursor: pointer; }
.option:hover { background: #f5f7fa; }
.option.empty { color: #999; cursor: default; }

.tabs {
  display:flex;
  margin: 18px 16px 0;
  background:#fff;
  border-radius:12px;
  box-shadow:0 2px 8px #f0f0f0;
}

.tab {
  flex:1;
  text-align:center;
  padding:14px 0;
  color:#888;
  border-bottom:3px solid transparent;
}

.tab.active {
  color:#238aff;
  border-bottom-color:#238aff;
  font-weight:bold;
  background:#eaf3ff;
  border-radius:0 0 8px 8px;
}

/* 优惠券卡片容器 */
.coupon-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin: 18px 16px 0;
  padding: 0 0 80px;
}

/* 优惠券卡片基础样式 */
.coupon-card {
  background: linear-gradient(135deg, #ff6b6b, #ffa500);
  border-radius: 16px;
  padding: 20px 16px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.coupon-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.coupon-card:active {
  transform: translateY(0);
}

/* 代金券样式 */
.cash-card {
  background: linear-gradient(135deg, #ff9a56, #ffad56);
}

/* 时长券样式 */
.time-card {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

/* 全免券样式 */
.free-card {
  background: linear-gradient(135deg, #43e97b, #38f9d7);
}

/* 折扣券样式 */
.percent-card {
  background: linear-gradient(135deg, #fa709a, #fee140);
}

/* 默认样式 */
.default-card {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

/* 卡片内容样式 */
.coupon-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.coupon-value {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #238aff;
  text-align: center;
  white-space: nowrap;
}

.coupon-validity {
  font-size: 12px;
  color: #666;
  text-align: center;
}

/* 加载状态 */
.loading {
  text-align: center;
  padding: 40px 0;
}

.loading-text {
  color: #888;
  font-size: 16px;
}

/* 空状态 */
.empty {
  text-align: center;
  padding: 60px 20px;
}

.empty-text {
  color: #999;
  font-size: 16px;
}

/* 自定义创建优惠券按钮样式 */
.btn {
  display: block;
  margin: 16px auto 0;
  padding: 12px 24px;
  border: none;
  border-radius: 28px;
  background: linear-gradient(135deg, #5aa3ff, #86e3ff);
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 1px;
  box-shadow: 0 8px 20px rgba(35, 138, 255, 0.25);
  cursor: pointer;
}
.btn:hover { filter: brightness(1.02); }
.btn:active { transform: translateY(1px); box-shadow: 0 6px 16px rgba(35,138,255,0.22); }
</style>


